import Enumerable from "linq";
import store from "../../../../store";
import { IInternalScriptProcessible } from "..";
import { ExecuteRequest, ExternalScriptExecuteResult, ScriptLinkItem } from "../../../../domain/localhost/items";
import { Arguments } from ".";
import { LoggerFactory } from "../../../../utilities/logger";
import { Profession, User } from "../../../../domain/user";

export class ProfessionGetter implements IInternalScriptProcessible {
  public isProcessible(request: ExecuteRequest): boolean {
    const configuration = store.state.configuration.behavior.localhost.internalScript.scripts.professionGetter;
    return configuration.isEnable
        && (new RegExp(configuration.pathExpression, "i")).test(this.toModuleName(request.exePath))
        && this.isLoginUser(request);
  }

  private isLoginUser(request: ExecuteRequest): boolean {
    try {
      const args = this.toArguments(request);
      const userId = args.getValue("-uid");
      const loginUser = store.getters.user as User;
      return loginUser.id.toUpperCase() === userId.toUpperCase();
    } catch (e) {
      LoggerFactory.getLogger().warn(e);
      return false;
    }
  }

  private toArguments(request: ExecuteRequest): Arguments {
    return new Arguments(request.arguments);
  }

  private toModuleName(path: string): string {
    if (!path) { return path; }
    const splitted = path.split("\\");
    return Enumerable.from(splitted).lastOrDefault();
  }

  public async process(request: ExecuteRequest): Promise<ExternalScriptExecuteResult> {
    if (!this.isLoginUser(request)) { throw new Error("ログインユーザでは無い職員の職種を取得することはできません。"); }
    const args = this.toArguments(request);
    const overwriteParameter = args.getValue("-overwrite");
    // デフォルトでは上書き許可する
    const shouldOverwriteValue = (!overwriteParameter || overwriteParameter.toLowerCase() === "true");
    const professionIdObjectName = args.getValue("-professionIdObject");
    const professionNameObjectName = args.getValue("-professionNameObject");
    const loginUser = store.getters.user as User;
    return this.toResult(request.scriptLinkItems, loginUser.profession, shouldOverwriteValue, professionIdObjectName, professionNameObjectName);
  }

  private toResult(scriptLinkItems: ScriptLinkItem[], profession: Profession, shouldOverwriteValue: boolean, professionIdObjectName: string, professionNameObjectName: string): ExternalScriptExecuteResult {
    const result = new ExternalScriptExecuteResult();
    result.isComplete = true;
    result.name = undefined;
    result.values = [];
    result.compressedCsvText = "";
    result.scriptLinkResults = this.toScriptLinkResults(scriptLinkItems, profession, shouldOverwriteValue, professionIdObjectName, professionNameObjectName);
    return result;
  }

  private toScriptLinkResults(scriptLinkItems: ScriptLinkItem[], profession: Profession, shouldOverwriteValue: boolean, professionIdObjectName: string, professionNameObjectName: string): ScriptLinkItem[] {
    const professionName = (profession && profession.name)
      ? profession.name
      : "";
    const professionId = (profession && profession.id)
      ? profession.id
      : "";
    
    const results: ScriptLinkItem[] = [];
    for (const item of scriptLinkItems) {
      if (!shouldOverwriteValue && !!item.value) { continue; }
      if (!professionIdObjectName && !professionNameObjectName) {
        // 対象のオブジェクト名称が指定されていない場合は、全ての項目に職種名称をセット
        results.push(this.toClonedItem(item, professionName));
      } else {
        // 指定された項目に職種IDもしくは職種名称をセット
        if (professionIdObjectName && item.name === professionIdObjectName) {
          results.push(this.toClonedItem(item, professionId));
        }
        if (professionNameObjectName && item.name === professionNameObjectName) {
          results.push(this.toClonedItem(item, professionName));
        }
      }
    }
    return results;
  }

  private toClonedItem(item: ScriptLinkItem, value: string): ScriptLinkItem {
    const cloned = item.clone();
    cloned.value = value;
    return cloned;
  }
}
